import {Component} from "@theme/component";
import {formatMoneyWithCurrency} from '@theme/utilities';
import {ThemeEvents, QuantitySelectorUpdateEvent} from '@theme/events';

/**
 * Computes the real, stock-aware max quantity for a quantity input, combining
 * any configured order-quantity rule with actual inventory availability.
 * Shopify's `quantity_rule.max` is a merchant-configured order rule and is
 * usually unset, so without this most variants would have no live cap at all.
 * @param {HTMLInputElement} input
 * @returns {string | null}
 */
function getStockCappedMax(input) {
  const ruleMax = input.max ? parseInt(input.max, 10) : null;

  const isInventoryTracked = input.dataset.inventoryManagement === 'shopify';
  const blocksOverselling = input.dataset.inventoryPolicy === 'deny';
  const inventoryQuantity =
    input.dataset.inventoryQuantity !== undefined && input.dataset.inventoryQuantity !== ''
      ? parseInt(input.dataset.inventoryQuantity, 10)
      : null;
  const stockMax = isInventoryTracked && blocksOverselling && inventoryQuantity !== null ? inventoryQuantity : null;

  if (ruleMax === null) return stockMax === null ? null : String(stockMax);
  if (stockMax === null) return String(ruleMax);
  return String(Math.min(ruleMax, stockMax));
}

/**
 * Shows a "maximum available" warning when the shopper's requested quantity
 * exceeds what the selected grouped variant can actually fulfil.
 * @param {Element} scope - Element to look for the warning node within.
 * @param {number} requestedValue - The quantity the shopper was trying to set.
 * @param {string | null} stockCappedMax - The real cap, from getStockCappedMax.
 * @param {string} errorTemplate - Template containing a `{{ maximum }}` placeholder.
 */
function showStockWarningIfNeeded(scope, requestedValue, stockCappedMax, errorTemplate) {
  const warningEl = /** @type {HTMLElement | null} */ (scope.querySelector('[data-stock-warning]'));
  if (!warningEl) return;

  const max = stockCappedMax !== null ? parseInt(stockCappedMax, 10) : null;

  if (max === null || requestedValue <= max) {
    warningEl.hidden = true;
    warningEl.textContent = '';
    return;
  }

  warningEl.textContent = errorTemplate.replace('{{ maximum }}', String(max));
  warningEl.hidden = false;
}

/**
 * @returns {HTMLInputElement | null} The main product's own quantity input.
 */
function getMainQuantityInput() {
  return /** @type {HTMLInputElement | null} */ (
    document.querySelector('product-form-component quantity-selector-component [data-quantity-input]')
  );
}

/**
 * @returns {HTMLInputElement | null} The hidden `items[1][quantity]` field submitted with the cart form.
 */
function getGroupQuantityHiddenInput() {
  return /** @type {HTMLInputElement | null} */ (document.querySelector('[data-product-group-variant-quantity]'));
}

/**
 * @returns {HTMLInputElement | null} The hidden `items[1][id]` field submitted with the cart form.
 */
function getGroupVariantIdHiddenInput() {
  return /** @type {HTMLInputElement | null} */ (document.querySelector('[data-product-group-variant-id]'));
}

export class ProductGroupSelector extends Component {
  #activeShape = null;
  #matchingOptionsCount = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.toggleGroupSelector);
    this.addEventListener('product-group-selection-confirmed', this.setGroupSelectorAsSelected);
    this.addEventListener('product-group-selected', this.setGroupSelectorSelectedId);

    document.addEventListener('change', this.handleGlobalVariantChange);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate);

    const dialogComponent = this.querySelector('#product-group-selector-dialog');
    if (dialogComponent) dialogComponent.addEventListener('dialog:open', this.handleDialogOpen);

    // Defer so ProductGroupSelectorModal has a chance to connect and attach its change listener first
    setTimeout(() => {
      this.filterModalOptions(this.dataset.currentSize);
    }, 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.toggleGroupSelector);
    this.removeEventListener('product-group-selection-confirmed', this.setGroupSelectorAsSelected);
    this.removeEventListener('product-group-selected', this.setGroupSelectorSelectedId);
    document.removeEventListener('change', this.handleGlobalVariantChange);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate);
  }

  toggleGroupSelector = (event) => {
    event.preventDefault();

    const inputType = event.target.hasAttribute('data-product-option') ? 'option' : 'toggle';
    const value = event.target.value;
    const hiddenInput = getGroupVariantIdHiddenInput();
    const canOpenModal = event.target.hasAttribute('data-product-group-toggle-with-id');

    if (canOpenModal) this.openModal();
    if (inputType === 'option') return;
    if (!hiddenInput) return;
    if (value) hiddenInput.value = value;

    hiddenInput.disabled = !value;
    if (!value) this.toggleSummary(false);

    this.syncGroupQuantity(!!value);
  }

  syncGroupQuantity = (/** @type {boolean} */ enabled) => {
    const groupQuantityInput = getGroupQuantityHiddenInput();
    if (!groupQuantityInput) return;

    groupQuantityInput.disabled = !enabled;
    if (enabled) {
      const mainQuantityInput = getMainQuantityInput();
      groupQuantityInput.value = mainQuantityInput ? mainQuantityInput.value : '1';
    }
  }

  /**
   * Keeps the grouped product's quantity in sync with the main quantity, unless
   * the shopper is adjusting it independently inside the group-selector modal.
   * @param {Event} event - The event.
   */
  handleQuantityUpdate = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('product-group-selector-modal')) return;

    const groupQuantityInput = getGroupQuantityHiddenInput();
    if (!groupQuantityInput || groupQuantityInput.disabled) return;

    const { quantity } = /** @type {QuantitySelectorUpdateEvent} */ (event).detail;
    groupQuantityInput.value = String(quantity);
  }

  /**
   * Prefills the modal's own quantity selector with the main product's current quantity.
   */
  syncModalQuantity = () => {
    const mainQuantityInput = getMainQuantityInput();
    const modalQuantitySelector = /** @type {any} */ (this.querySelector('product-group-selector-modal quantity-selector-component'));
    if (!mainQuantityInput || !modalQuantitySelector) return;

    const requestedValue = parseInt(mainQuantityInput.value, 10) || 1;
    modalQuantitySelector.setValue(mainQuantityInput.value);

    // Re-snap to whatever min/max/step is already on the modal's input, so a stale
    // (too high/low) value from a previous option doesn't survive the prefill.
    const modalQuantityInputEl = /** @type {HTMLInputElement} */ (modalQuantitySelector.refs.quantityInput);
    const stockCappedMax = getStockCappedMax(modalQuantityInputEl);
    modalQuantitySelector.updateConstraints(
      modalQuantityInputEl.min || '1',
      stockCappedMax,
      modalQuantityInputEl.step || '1'
    );

    const modal = /** @type {HTMLElement | null} */ (this.querySelector('product-group-selector-modal'));
    if (modal) showStockWarningIfNeeded(modal, requestedValue, stockCappedMax, modal.dataset.quantityErrorMax || '');
  }

  toggleSummary = (show) => {
    const summary = document.querySelector('[data-product-group-summary]');
    if (summary) summary.hidden = !show;
  }

  openModal = () => {
    const dialogComponent = document.getElementById('product-group-selector-dialog');

    if (dialogComponent) dialogComponent.showDialog();
  }

  setGroupSelectorSelectedId = (event) => {
    const { variantId } = event.detail;
    const optionInput = this.querySelector('[data-product-group-toggle-with-id]');

    if (optionInput) optionInput.value = variantId;
  }

  setGroupSelectorAsSelected = (event) => {
    const optionInput = this.querySelector(`[data-product-group-toggle-with-id]`);

    if (optionInput) optionInput.checked = true;

    const { title } = event.detail;
    this.updateSummaryContent({ title });
    this.toggleSummary(this.#matchingOptionsCount > 0);
  };

  updateSummaryContent = ({ title }) => {
    const summaryOption = document.querySelector('[data-product-group-summary-option]');
    if (summaryOption) {
      summaryOption.textContent = title;
    }
  }

  handleGlobalVariantChange = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    const variantPicker = target.closest(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantPicker) return;
    if (this.parseShape(target.value) !== null) {
      this.filterModalOptions(target.value);
    }
  }

  handleDialogOpen = () => {
    const allOptions = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('[data-product-option]')]);
    if (allOptions.length === 0) return;
    const checkedOption = allOptions.find(o => o.checked);
    if (!checkedOption) return;

    const modal = /** @type {HTMLElement | null} */ (this.querySelector('product-group-selector-modal'));
    const isAlreadyLoaded = modal && modal.dataset.loadedProductId === checkedOption.value;

    this.syncModalQuantity();

    if (!isAlreadyLoaded) {
      checkedOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  filterModalOptions = (sizeValue) => {
    const shape = this.parseShape(sizeValue);
    this.#activeShape = shape;

    const options = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('[data-product-option]'));
    /** @type {HTMLInputElement | undefined} */
    let firstMatch;
    let matchingCount = 0;

    options.forEach(option => {
      const label = option.closest('label');
      if (!label) return;
      const optionShape = option.dataset.variantOption || '';
      const matches = !shape || optionShape.toLowerCase() === shape.toLowerCase();
      label.style.display = matches ? '' : 'none';
      if (matches) matchingCount++;
      if (matches && !firstMatch) firstMatch = option;
    });

    this.#matchingOptionsCount = matchingCount;

    if (shape && firstMatch) {
      const needsUpdate = !firstMatch.checked;
      firstMatch.checked = true;
      if (needsUpdate) firstMatch.dispatchEvent(new Event('change', { bubbles: true }));
      const toggleInput = this.querySelector('[data-product-group-toggle-with-id]');
      if (toggleInput) toggleInput.value = firstMatch.value;
    }
  }

  parseShape = (sizeValue) => {
    if (!sizeValue) return null;
    const match = sizeValue.match(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i);
    if (!match) return null;
    return parseFloat(match[1]) === parseFloat(match[2]) ? 'Square' : 'Rectangle';
  }
}

if (!customElements.get('product-group-selector')) {
  customElements.define('product-group-selector', ProductGroupSelector);
}

export class ProductGroupSelectorModal extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.fetchProduct);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.fetchProduct);
    this.#abortController?.abort();
  }

  /**
   * Handles submit button click
   * @param {Event} event - The click event
   */
  confirmSelection = async (event) => {
    event.preventDefault();

    const selectedOption = this.querySelector('[data-product-option]:checked');
    const input = getGroupVariantIdHiddenInput();

    if (!input && !selectedOption) return;

    input.value = selectedOption.value;
    input.disabled = false;

    const groupQuantityInput = getGroupQuantityHiddenInput();
    if (groupQuantityInput) {
      const modalQuantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-quantity-input]'));
      groupQuantityInput.value = modalQuantityInput ? modalQuantityInput.value : '1';
      groupQuantityInput.disabled = false;
    }

    this.closeModal();
    this.dispatchEvent(new CustomEvent('product-group-selection-confirmed', {
      bubbles: true,
      detail: {
        title: selectedOption.dataset.productTitle,
      }
    }));
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  fetchProduct = async (event) => {
    event.preventDefault();

    const currentUrl = event.target.dataset.productUrl;
    const optionPrice = event.target.dataset.productPrice;
    const value = event.target.value;

    // Skeleton only on first load; later swaps replace content directly, no flash.
    const hasLoadedBefore = this.dataset.loadedProductId !== undefined;
    if (!hasLoadedBefore) this.classList.add('is-loading-accordions');

    const html = await this.fetchProductPage(currentUrl);

    if (html) {
      this.updateProductContent(html, optionPrice);
      this.updateQuantityConstraints(html);
      this.updateSelectedOptionId(value);
      this.dataset.loadedProductId = value;
      this.classList.remove('is-loading-accordions');
    }
  };

  /**
   * Applies the newly selected option's own quantity rules (min/max/step, stock)
   * to the modal's quantity selector, instead of leaving it pinned to whichever
   * option loaded first.
   * @param {Document} html - The fetched product page.
   */
  updateQuantityConstraints(html) {
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (html.querySelector('quantity-selector-component [data-quantity-input]'));
    const modalQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (!newQuantityInput || !modalQuantitySelector) return;

    const modalQuantityInputEl = /** @type {HTMLInputElement} */ (modalQuantitySelector.refs.quantityInput);

    // Re-anchor to the main product's quantity on every option switch, instead of
    // carrying over whatever a previous (possibly more restrictive) option left
    // behind — so a generous option C isn't stuck at option B's lower quantity.
    const mainQuantityInput = getMainQuantityInput();
    const requestedValue = mainQuantityInput ? parseInt(mainQuantityInput.value, 10) || 1 : parseInt(modalQuantityInputEl.value, 10) || 1;
    modalQuantitySelector.setValue(String(requestedValue));

    // Carry the newly selected option's own inventory data onto the modal's
    // persisted input, so reopening the modal later (without a re-fetch) still
    // has accurate data to clamp against.
    modalQuantityInputEl.dataset.inventoryManagement = newQuantityInput.dataset.inventoryManagement || '';
    modalQuantityInputEl.dataset.inventoryPolicy = newQuantityInput.dataset.inventoryPolicy || '';
    modalQuantityInputEl.dataset.inventoryQuantity = newQuantityInput.dataset.inventoryQuantity || '';

    // Set directly (not via setCartQuantity()) to avoid re-clamping against the
    // previous option's max before updateConstraints() below installs the new one.
    modalQuantityInputEl.setAttribute('data-cart-quantity', (parseInt(newQuantityInput.dataset.cartQuantity || '0', 10)).toString());

    const stockCappedMax = getStockCappedMax(newQuantityInput);
    modalQuantitySelector.updateConstraints(
      newQuantityInput.min || '1',
      stockCappedMax,
      newQuantityInput.step || '1'
    );

    showStockWarningIfNeeded(this, requestedValue, stockCappedMax, this.dataset.quantityErrorMax || '');
  }

  updateSelectedOptionId(value) {
    this.dispatchEvent(new CustomEvent('product-group-selected', {
      bubbles: true,
      detail: { variantId: value }
    }));
  }

  closeModal = () => {
    const dialogComponent = document.getElementById('product-group-selector-dialog');

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();

      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} html
   */
  async updateProductContent(html, optionPrice) {
    const image = html.querySelector('[data-product-group-image] img');
    const currentImage = this.querySelector('[data-product-image] img');
    const accordions = html.querySelectorAll('.section-custom .details-content');
    const currentAccordions = this.querySelectorAll('[data-accordion-content]');
    const mainPrice = this.dataset.mainProductPrice;
    const priceElement = this.querySelector('[data-group-price]');

    if (image && currentImage) currentImage.replaceWith(image.cloneNode(true));

    currentAccordions.forEach((accordion, index) => {
      const newAccordion = accordions[index]?.querySelector('.text-block:not(.text-block--empty)');
      const accordionContainer = accordion.closest('.product-group-selector-accordion');
      const accordionHidden = accordionContainer.classList.contains("product-group-selector-accordion--hidden");

      if (newAccordion) accordion.innerHTML = newAccordion.innerHTML;
      else accordion.innerHTML = '';

      if (accordionHidden && newAccordion) {
        accordionContainer.classList.remove("product-group-selector-accordion--hidden");
      }

      if (!accordionHidden && !newAccordion) {
        accordionContainer.classList.add("product-group-selector-accordion--hidden");
      }
    });

    if (optionPrice && priceElement) {
      const price = parseFloat(optionPrice) + parseFloat(mainPrice);
      priceElement.textContent = formatMoneyWithCurrency(String(price));
    }
  }
}

if (!customElements.get('product-group-selector-modal')) {
  customElements.define('product-group-selector-modal', ProductGroupSelectorModal);
}